import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AdminService, PersonPicker } from '../../../core/admin/admin';
import { PeopleService, Person, PersonSearchResult, ageFor } from '../../../core/people/people';

/** The people who picked this person in one competition, for the research panel. */
interface PickerGroup {
  competition: PersonPicker['competition'];
  contestants: { displayName: string; isAlternate: boolean }[];
}

/**
 * View/edit/delete a single catalog Person, or add a new one. Mounted/
 * unmounted by the parent (via @if) — fetches on init, same idiom as
 * EntriesModal/ContestantModal, since a new instance is created each time
 * it's opened for a different person.
 *
 * Two ways to open it:
 *  - `personId` set — loads and edits an existing catalog row.
 *  - `personId` omitted, `prefill` optionally set — starts blank (or
 *    pre-filled from a person-search result, local or Wikipedia) and Save
 *    creates the row via PeopleService.select(), the same endpoint the
 *    picks page uses to persist a Wikipedia candidate.
 *
 * Save closes the modal on success either way (create or edit) — see
 * save() below.
 */
@Component({
  selector: 'app-person-modal',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './person-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './person-modal.css',
})
export class PersonModal implements OnInit {
  private readonly people = inject(PeopleService);
  private readonly admin = inject(AdminService);

  readonly personId = input<number | null>(null);
  readonly prefill = input<PersonSearchResult | null>(null);
  readonly closed = output<void>();
  /** Emitted after a successful save or delete, so the parent list can refresh. */
  readonly changed = output<void>();

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly person = signal<Person | null>(null);

  /** Research panel (existing person only): everyone who has picked them,
   *  already sorted by the API. Loaded alongside the person and independent
   *  of it — a failure here never blocks viewing or editing the person. */
  readonly pickers = signal<PersonPicker[]>([]);
  readonly pickersLoading = signal(false);
  readonly pickersError = signal<string | null>(null);

  /** The sorted pickers grouped under their competition (the order is kept). */
  readonly pickerGroups = computed<PickerGroup[]>(() => {
    const groups: PickerGroup[] = [];
    for (const p of this.pickers()) {
      let group = groups.find((g) => g.competition.id === p.competition.id);
      if (!group) {
        group = { competition: p.competition, contestants: [] };
        groups.push(group);
      }
      group.contestants.push({
        displayName: p.contestant.displayName,
        isAlternate: p.isAlternate,
      });
    }
    return groups;
  });

  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);

  readonly confirmingDelete = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true }),
    knownFor: new FormControl('', { nonNullable: true }),
    /** F-033 — comma-separated in the UI, parsed via parseNames() on save
     *  (same convention as the bulk-add-picks panel). */
    aliases: new FormControl('', { nonNullable: true }),
    dateOfBirth: new FormControl('', { nonNullable: true }),
    dateOfDeath: new FormControl('', { nonNullable: true }),
    causeOfDeath: new FormControl('', { nonNullable: true }),
    wikipediaUrl: new FormControl('', { nonNullable: true }),
    imageUrl: new FormControl('', { nonNullable: true }),
  });

  async ngOnInit(): Promise<void> {
    const id = this.personId();
    if (id === null) {
      // Add flow — nothing to fetch; start the form from the search
      // candidate (if any) and let the admin review/edit before the first
      // Save creates the row.
      const p = this.prefill();
      this.form.setValue({
        name: p?.name ?? '',
        knownFor: p?.knownFor ?? '',
        aliases: (p?.aliases ?? []).join(', '),
        dateOfBirth: this.dateOnly(p?.dateOfBirth ?? null),
        dateOfDeath: this.dateOnly(p?.dateOfDeath ?? null),
        causeOfDeath: p?.causeOfDeath ?? '',
        wikipediaUrl: p?.wikipediaUrl ?? '',
        imageUrl: p?.imageUrl ?? '',
      });
      this.loading.set(false);
      return;
    }

    void this.loadPickers(id);

    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const person = await this.people.getOne(id);
      this.person.set(person);
      this.form.setValue({
        name: person.name,
        knownFor: person.knownFor ?? '',
        aliases: person.aliases.join(', '),
        dateOfBirth: this.dateOnly(person.dateOfBirth),
        dateOfDeath: this.dateOnly(person.dateOfDeath),
        causeOfDeath: person.causeOfDeath ?? '',
        wikipediaUrl: person.wikipediaUrl ?? '',
        imageUrl: person.imageUrl ?? '',
      });
    } catch {
      this.errorMessage.set('Could not load this person — try again.');
    } finally {
      this.loading.set(false);
    }
  }

  close(): void {
    this.closed.emit();
  }

  /** A death date makes the person deceased — same rule the API uses to
   *  derive status. Read from the form, so it follows the admin's edits. */
  isDeceased(): boolean {
    return this.form.controls.dateOfDeath.value.trim() !== '';
  }

  /** "(46 years old)" — their current age while alive, or their age at
   *  death once there's a death date. Null when it can't be worked out (no
   *  birth date yet, or a birth date after the death date). Shown beside the
   *  Date of birth label for the living and the Date of death label for the
   *  deceased. */
  ageText(): string | null {
    const { dateOfBirth, dateOfDeath } = this.form.getRawValue();
    const age = ageFor({
      dateOfBirth: dateOfBirth.trim() || null,
      dateOfDeath: dateOfDeath.trim() || null,
    });
    if (age === null || age < 0) return null;
    return `(${age} ${age === 1 ? 'year' : 'years'} old)`;
  }

  competitionDates(c: PersonPicker['competition']): string {
    return `${this.dateOnly(c.startDate)} – ${this.dateOnly(c.endDate)}`;
  }

  private async loadPickers(personId: number): Promise<void> {
    this.pickersLoading.set(true);
    this.pickersError.set(null);
    try {
      this.pickers.set(await this.admin.listPersonPickers(personId));
    } catch {
      this.pickersError.set('Could not load who picked this person.');
    } finally {
      this.pickersLoading.set(false);
    }
  }

  /** Blank fields clear the corresponding nullable column; a non-blank
   *  value sets it. Simpler than a separate "clear" affordance per field.
   *  When there's no existing person yet, this creates one instead (via
   *  the same PeopleService.select() the picks page uses). Either way,
   *  a successful save closes the modal. */
  async save(): Promise<void> {
    const current = this.person();

    this.saving.set(true);
    this.saveError.set(null);
    const raw = this.form.getRawValue();
    const aliases = parseNames(raw.aliases);
    try {
      const updated = current
        ? await this.people.update(current.id, {
            name: raw.name.trim(),
            knownFor: raw.knownFor.trim() || null,
            aliases,
            dateOfBirth: raw.dateOfBirth.trim() || null,
            dateOfDeath: raw.dateOfDeath.trim() || null,
            causeOfDeath: raw.causeOfDeath.trim() || null,
            wikipediaUrl: raw.wikipediaUrl.trim() || null,
            imageUrl: raw.imageUrl.trim() || null,
          })
        : await this.people.select({
            name: raw.name.trim(),
            knownFor: raw.knownFor.trim() || undefined,
            aliases,
            dateOfBirth: raw.dateOfBirth.trim() || undefined,
            dateOfDeath: raw.dateOfDeath.trim() || undefined,
            causeOfDeath: raw.causeOfDeath.trim() || undefined,
            wikipediaUrl: raw.wikipediaUrl.trim() || undefined,
            imageUrl: raw.imageUrl.trim() || undefined,
          });
      this.person.set(updated);
      this.changed.emit();
      this.closed.emit();
    } catch (err) {
      this.saveError.set(this.errorMessageFor(err));
    } finally {
      this.saving.set(false);
    }
  }

  async confirmDelete(): Promise<void> {
    const current = this.person();
    if (!current) return;

    this.deleting.set(true);
    this.deleteError.set(null);
    try {
      await this.people.remove(current.id);
      this.changed.emit();
      this.closed.emit();
    } catch (err) {
      this.deleteError.set(this.errorMessageFor(err));
      this.confirmingDelete.set(false);
    } finally {
      this.deleting.set(false);
    }
  }

  /** The API returns a full ISO datetime (e.g. "1950-01-01T00:00:00.000Z")
   *  for a Prisma DateTime column, but `<input type="date">` only accepts
   *  an exact "YYYY-MM-DD" value — anything else is silently rejected and
   *  the field just renders blank, with no error. Truncating here is what
   *  makes an existing date actually show up in the form. */
  private dateOnly(iso: string | null): string {
    return iso ? iso.slice(0, 10) : '';
  }

  private errorMessageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 409) {
        return (
          (err.error as { message?: string } | null)?.message ??
          'This person is still referenced elsewhere — remove that first.'
        );
      }
      if (err.status === 404) {
        return 'That person no longer exists.';
      }
    }
    return 'Something went wrong — try again in a moment.';
  }
}

/** Splits on commas, trims, drops empties, dedupes case-insensitively —
 *  copied from bulk-add-people-modal.ts/entries-modal.ts (kept local since
 *  it's five lines and these features otherwise don't interact). */
function parseNames(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const name = part.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}
